<template>
  <v-dialog :value="value" max-width="1100" scrollable @input="$emit('input', $event)" content-class="glass-dialog-wrapper">
    <v-card class="glass-card-dark elevation-24 d-flex flex-column" style="height: 85vh">
      <div class="relative flex-shrink-0">
        <div class="customer-bg" :style="{ backgroundImage: `url(${getPicture(editData.customer)})` }"></div>
        <div class="customer-overlay"></div>

        <div class="relative z-index-2 px-6 pt-6 pb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center" v-if="editData.customer">
            <v-avatar size="80" class="elevation-10 mr-4 border-white">
              <img :src="getPicture(editData.customer)" style="object-fit: cover" />
            </v-avatar>
            <div>
              <h2 class="text-h4 font-weight-black white--text text-shadow mb-0 line-height-1">
                {{ editData.customer.vorname }} {{ editData.customer.nachname }}
              </h2>
              <div class="d-flex align-center mt-1">
                <v-chip x-small color="rgba(255,255,255,0.2)" class="mr-2 backdrop-blur">
                  <v-icon x-small left>mdi-cake</v-icon> {{ calculateAge(editData.customer.geburtstag) }}
                </v-chip>
                <v-chip x-small color="rgba(255,255,255,0.2)" class="backdrop-blur" v-if="editData.customer.city">
                  <v-icon x-small left>mdi-map-marker</v-icon> {{ editData.customer.city }}
                </v-chip>

                <v-chip v-if="isOnline" x-small color="green accent-4" class="ml-2 font-weight-bold black--text">
                  <v-icon x-small left color="black">mdi-circle</v-icon> Im Studio
                </v-chip>
              </div>
            </div>
          </div>
          <div v-else class="text-h5 white--text font-weight-bold">
            <v-icon :color="editData.color" class="mr-2">{{ editData.isTask ? "mdi-checkbox-marked" : "mdi-calendar" }}</v-icon>
            {{ editData.isTask ? "Aufgabe" : "Termin" }}
          </div>

          <div class="d-flex align-center">
            <v-btn v-if="editData.customer" depressed color="primary" class="mr-3 font-weight-bold" @click="openPanel">
              <v-icon left small>mdi-view-dashboard</v-icon> Zum Panel
            </v-btn>
            <v-btn icon color="white" class="glass-btn" @click="$emit('input', false)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-card-text class="pt-4 pb-0 flex-grow-1 overflow-hidden" style="display: flex">
        <v-row class="fill-height ma-0">
          <v-col cols="12" md="7" class="custom-scrollbar pr-4" style="overflow-y: auto; height: 100%">
            <div v-if="loading && editData.customer" class="py-4">
              <v-skeleton-loader dark type="article, list-item-three-line" class="custom-skeleton mb-4 transparent"></v-skeleton-loader>
            </div>

            <div v-else-if="editData.customer && customerDetails">
              <div class="mb-6">
                <div class="caption grey--text text-uppercase font-weight-bold mb-2">Aktionen</div>

                <v-btn
                  block
                  x-large
                  :color="mainAction.color"
                  class="white--text font-weight-black elevation-10 glow-hover mb-3 rounded-xl"
                  @click="executeAction(mainAction.type)">
                  <v-icon left large class="mr-4">{{ mainAction.icon }}</v-icon>
                  <div class="d-flex flex-column align-start">
                    <span class="text-h6 font-weight-black line-height-1">{{ mainAction.text }}</span>
                    <span class="text-caption font-weight-regular opacity-80">{{ mainAction.subtext }}</span>
                  </div>
                  <v-spacer></v-spacer>
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>

                <div class="d-flex gap-2">
                  <v-btn
                    v-for="act in secondaryActions"
                    :key="act.type"
                    small
                    depressed
                    color="rgba(255,255,255,0.05)"
                    class="flex-grow-1 py-4 rounded-lg"
                    @click="executeAction(act.type)">
                    <v-icon left small :color="act.color">{{ act.icon }}</v-icon>
                    <span class="grey--text text--lighten-1">{{ act.label }}</span>
                  </v-btn>
                </div>
              </div>

              <div class="mb-4 d-flex flex-wrap gap-2" v-if="activeTimestop">
                <v-alert
                  dense
                  type="warning"
                  color="orange darken-4"
                  icon="mdi-pause-circle"
                  class="width-100 rounded-lg text-caption font-weight-bold white--text ma-0">
                  Pausiert bis {{ formatDate(activeTimestop.timestopInfos.end) }}
                </v-alert>
              </div>

              <v-card outlined class="glass-panel-light pa-4 rounded-lg mb-4">
                <div class="d-flex justify-space-between align-start">
                  <div>
                    <div class="caption grey--text mb-1">Vertrag</div>
                    <div class="white--text font-weight-bold text-subtitle-1 line-height-1 mb-1">
                      {{ customerDetails.mainContract ? customerDetails.mainContract.NAME : "Kein Vertrag" }}
                    </div>
                    <div v-if="customerDetails.mainContract" class="green--text text-caption font-weight-bold">
                      Bis {{ formatDate(customerDetails.mainContract.TERM_ENDDATE) }}
                    </div>
                  </div>
                  <v-icon color="grey darken-2">mdi-file-document-outline</v-icon>
                </div>
                <div v-if="customerDetails.packages && customerDetails.packages.length" class="mt-3 pt-3 border-top-light">
                  <div class="d-flex flex-wrap gap-2">
                    <v-chip
                      v-for="pkg in customerDetails.packages"
                      :key="pkg.CONTRACTID"
                      x-small
                      color="blue darken-3"
                      label
                      class="font-weight-bold border-blue">
                      {{ pkg.NAME }}
                    </v-chip>
                  </div>
                </div>
              </v-card>

              <div v-if="customerDetails.customerInteractions?.length">
                <div class="caption grey--text text-uppercase font-weight-bold mb-2">Letzte Kontakte</div>
                <div class="custom-scrollbar pr-2" style="max-height: 150px; overflow-y: auto">
                  <div
                    v-for="ia in customerDetails.customerInteractions.slice(0, 5)"
                    :key="ia._id"
                    class="mb-2 glass-panel-light pa-3 rounded-lg relative">
                    <div class="d-flex justify-space-between align-center mb-1">
                      <div class="caption font-weight-bold blue--text text-truncate">{{ ia.interactions.reason }}</div>
                      <div class="caption grey--text" style="min-width: 60px; text-align: right">
                        {{ $moment(ia.interactions.date).format("DD.MM") }}
                      </div>
                    </div>
                    <div class="white--text text-caption mb-1 text-truncate">"{{ ia.interactions.notes }}"</div>
                    <div class="d-flex align-center justify-end">
                      <v-avatar size="16" class="mr-1"><img src="/img/default-avatar.png" /></v-avatar>
                      <span class="caption grey--text">{{ ia.employee.vorname }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="fill-height d-flex align-center justify-center flex-column text-center opacity-50">
              <v-icon size="64" color="grey">mdi-account-off</v-icon>
              <div class="mt-2">Kein Kunde verknüpft</div>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="pl-4 border-left-light d-flex flex-column" style="height: 100%">
            <div class="flex-grow-1 overflow-y-auto custom-scrollbar pr-2">
              <div class="d-flex align-center mb-4">
                <v-icon small color="grey" class="mr-2">mdi-pencil</v-icon>
                <span class="caption grey--text text-uppercase font-weight-bold">Termin Details</span>
              </div>

              <v-text-field v-model="localData.title" label="Titel" outlined dense dark class="rounded-lg mb-2"></v-text-field>

              <div class="glass-panel-light rounded-lg pa-3 mb-4">
                <div class="d-flex justify-space-between mb-2">
                  <v-text-field
                    v-model="localData.start"
                    label="Start"
                    type="time"
                    dense
                    dark
                    hide-details
                    flat
                    solo-inverted
                    class="time-input mr-2"></v-text-field>
                  <v-text-field
                    dark
                    v-if="!localData.isTask"
                    v-model="localData.duration"
                    label="Minuten"
                    type="number"
                    dense
                    hide-details
                    flat
                    solo-inverted
                    class="time-input"></v-text-field>
                </div>

                <div class="d-flex align-center mt-2">
                  <span class="caption grey--text mr-2">Farbe:</span>
                  <div class="d-flex flex-wrap gap-1">
                    <div
                      v-for="c in colorOptions"
                      :key="c"
                      class="color-dot cursor-pointer"
                      :class="{ active: localData.color === c }"
                      :style="{ background: c }"
                      @click="localData.color = c"></div>
                  </div>
                </div>
              </div>

              <v-textarea v-model="localData.description" label="Interne Notiz" outlined dense rows="8" dark class="rounded-lg"></v-textarea>
            </div>

            <div class="d-flex justify-space-between pt-4 mt-auto border-top-light">
              <v-btn color="red darken-2" text small @click="$emit('delete', localData)"> <v-icon left small>mdi-delete</v-icon> Löschen </v-btn>
              <v-btn color="success" depressed class="font-weight-bold px-6 rounded-lg" @click="save"> Speichern </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-dialog v-model="showAnamnese" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card class="black">
          <v-toolbar dark color="black">
            <v-btn icon @click="showAnamnese = false"><v-icon>mdi-close</v-icon></v-btn>
            <v-toolbar-title>Anamnese</v-toolbar-title>
          </v-toolbar>
          <anamnese-bogen v-if="showAnamnese" :user-id="editData.customer._id" :readonly="hasAnamnese" @close="showAnamnese = false"></anamnese-bogen>
        </v-card>
      </v-dialog>

      <upgrade-contract v-if="showUpgrade" v-model="showUpgrade" :user="customerDetails" @close="showUpgrade = false"></upgrade-contract>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment"
// Importiere deine existierenden Komponenten
import AnamneseBogen from "@/components/anamneseBogenCreate" // Pfad anpassen!
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract_sebredesign" // Dein File
import { mapActions } from "vuex"

export default {
  name: "WorkCalendarEditDialog",
  components: { AnamneseBogen, UpgradeContract },
  props: ["value", "editData", "customerDetails", "loading"],
  data() {
    return {
      localData: {},
      colorOptions: ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#607D8B", "#E91E63"],
      showAnamnese: false,
      showUpgrade: false,
    }
  },
  watch: {
    editData: {
      immediate: true,
      handler(val) {
        if (val) this.localData = JSON.parse(JSON.stringify(val))
      },
    },
  },
  computed: {
    isOnline() {
      return this.customerDetails && this.customerDetails.checkinDate && !this.customerDetails.checkoutDate
    },
    hasAnamnese() {
      return this.customerDetails?.documents?.anamnese?.length > 0
    },
    activeTimestop() {
      if (!this.customerDetails?.documents?.timestops) return null
      const now = moment()
      return this.customerDetails.documents.timestops.find((t) => {
        if (t.status !== "accepted") return false
        const start = moment(t.timestopInfos.start)
        const end = moment(t.timestopInfos.end)
        return now.isBetween(start, end, "day", "[]")
      })
    },

    // LOGIK FÜR HAUPT-ACTION
    mainAction() {
      const type = this.editData.specialType

      if (type === "workout") {
        return {
          type: "workout",
          text: "Trainingsplan erstellen",
          subtext: "",
          icon: "mdi-dumbbell",
          color: "primary",
        }
      }
      if (type === "anamnese") {
        if (this.hasAnamnese)
          return { type: "anamnese", text: "Anamnese ansehen", subtext: "Bereits vorhanden", icon: "mdi-clipboard-check", color: "teal darken-2" }
        return { type: "anamnese", text: "Anamnese starten", subtext: "Erfassung durchführen", icon: "mdi-clipboard-pulse", color: "teal accent-4" }
      }
      if (type === "upgrade") {
        return { type: "upgrade", text: "Upgrade durchführen", subtext: "Vertrag anpassen", icon: "mdi-star-shooting", color: "purple accent-3" }
      }

      // Default wenn kein Special Type, aber Kunde da
      return { type: "workout", text: "Training planen", subtext: "Standard Aktion", icon: "mdi-dumbbell", color: "blue" }
    },

    // SEKUNDÄRE AKTIONEN (ALLES WAS NICHT MAIN IST)
    secondaryActions() {
      const actions = [
        { type: "workout", label: "Plan", icon: "mdi-dumbbell", color: "green" },
        { type: "anamnese", label: "Anamnese", icon: "mdi-clipboard-pulse", color: "teal" },
        { type: "upgrade", label: "Upgrade", icon: "mdi-star", color: "purple" },
      ]
      return actions.filter((a) => a.type !== this.mainAction.type)
    },
  },
  methods: {
    ...mapActions("panelStore", ["selectCustomer"]),

    save() {
      this.$emit("save", this.localData)
    },
    getPicture() {
      if (this.customerDetails?.bild?.includes("uploads")) return "/img/profilbilder/" + this.customerDetails.bild
      if (this.customerDetails?.bild) return "data:image/jpeg;base64," + this.customerDetails.bild
      return "/img/icons/user_placeholder.png"
    },
    calculateAge(bd) {
      return bd ? moment().diff(moment(bd), "years") : "?"
    },
    formatDate(d) {
      return moment(d).format("DD.MM.YY")
    },

    openPanel() {
      this.selectCustomer(this.customerDetails)
      // Route zum Panel (User ist im Store durch WorkCalendar.vue schon gesetzt)
      this.$router.push("/panel")
    },

    executeAction(type) {
      if (type === "workout") {
        // Workout Logic (Route)
        this.$router.push(`/workouts/createnew?userId=${this.editData.customer._id}`)
      } else if (type === "anamnese") {
        // Open Component Overlay
        this.showAnamnese = true
      } else if (type === "upgrade") {
        // Open Component Overlay
        this.showUpgrade = true
      }
    },
  },
}
</script>

<style scoped>
.glass-dialog-wrapper {
  box-shadow: none;
}
.glass-card-dark {
  background: rgba(20, 20, 20, 0.98) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}
.glass-panel-light {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-btn {
  background: rgba(255, 255, 255, 0.1);
}

/* Customer Header */
.customer-bg {
  height: 100%;
  width: 100%;
  background-size: cover;
  background-position: center;
  position: absolute;
  top: 0;
  left: 0;
  filter: blur(20px) brightness(0.4);
  opacity: 0.5;
}
.customer-overlay {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(20, 20, 20, 1));
}
.border-white {
  border: 3px solid white !important;
}
.border-blue {
  border: 1px solid rgba(33, 150, 243, 0.5) !important;
}
.border-left-light {
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}
.border-top-light {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.text-shadow {
  text-shadow: 0 4px 10px rgba(0, 0, 0, 0.8);
}
.backdrop-blur {
  backdrop-filter: blur(5px);
}

.glow-hover:hover {
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.02);
  transition: 0.2s;
}
.line-height-1 {
  line-height: 1.1;
}
.color-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid transparent;
  transition: 0.2s;
}
.color-dot:hover {
  transform: scale(1.1);
}
.color-dot.active {
  border-color: white;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}
.gap-2 {
  gap: 8px;
}
.gap-1 {
  gap: 4px;
}
.width-100 {
  width: 100%;
}

.custom-skeleton >>> .v-skeleton-loader__image {
  height: 150px;
  border-radius: 12px;
}
.custom-skeleton >>> .v-skeleton-loader__text {
  background: rgba(255, 255, 255, 0.1);
}
.time-input >>> input {
  text-align: center;
  font-weight: bold;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 3px;
}
</style>
